import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-apollo';
import axios from 'axios';
import moment from 'moment';
import { toast } from 'react-toastify';
import ReactSVG from 'react-svg';
import { disableBodyScroll } from 'body-scroll-lock';
import ReactGA from 'react-ga';
import heart_path from 'heart_path.svg';
import FavoriteSuccessBanner from './CustomToast/FavoriteSuccessBanner';
import { handleHeartAnimation } from '../utils/handleHeartAnimation';
import { userQuery } from '../constants/queries';
import ErrorBoundary from './ErrorBoundary';
import { clientNoVarnish } from './client';

function EventCard({ event, eventCardMap, id }) {
  const eventImage =
    event.medium_featured_image_url ||
    event.small_featured_image_url ||
    event.featured_image.image_url;

  // Initialize ReactGA on page load
  useEffect(() => {
    ReactGA.initialize('UA-134642264-1');
  }, []);

  const { data: userData } = useQuery(userQuery, {
    fetchPolicy: 'no-cache',
    client: clientNoVarnish,
  });
  const heartRef = useRef(null);
  const cardRef = useRef(null);
  const [isFavorited, setFavorited] = useState(false);
  const [location, setLocation] = useState('');

  useEffect(() => {
    setLocation(window.location.origin);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line
    event.favorited && setFavorited(true);
  }, [event.favorited]);

  const [favoriteRoute, setFavoriteRoute] = useState(null);
  useEffect(() => {
    setFavoriteRoute(window.Routes.favorite_event_path({ id: event.id }));
  }, [event.id]);

  const handleFavorite = () => {
    const tokenElement = document.querySelector('[name=csrf-token]');
    if (userData && userData.user && userData.user.id) {
      handleHeartAnimation({ ref: heartRef, isFavorited });
      axios
        .patch(favoriteRoute, null, {
          headers: {
            'X-CSRF-Token': tokenElement ? tokenElement.content : '',
            Accept: 'application/json',
          },
        })
        .then(res => {
          const { pathname } = window.location;
          // If the event is NOT favorited yet, set favorited
          toast.dismiss();
          if (!isFavorited)
            toast(<FavoriteSuccessBanner favorited type={event} />);
          else toast(<FavoriteSuccessBanner type={event} />);

          if (res.status === 200) setFavorited(!isFavorited);

          if (
            pathname.includes('/account') ||
            pathname.includes('/favorites')
          ) {
            cardRef.current.style.opacity = 0;
            setTimeout(() => {
              cardRef.current.parentElement.parentElement.remove();
            }, 300);
          }
        })
        .catch(err => {
          console.error(err);
          if (err.response.status === 401) {
            window.location.pathname = window.Routes.login_path();
          }
        });
    } else {
      const modal = document.querySelector('#popup-modal');
      modal.classList.remove('fadeOut');
      modal.classList.remove('hidden');
      modal.classList.add('fadeIn');
      disableBodyScroll(modal);
    }
  };

  if (!document.querySelector('[name=csrf-token]').content) {
    console.warning('Could not find csrf-token');
  }

  const eventImageSplit = eventImage.split('.');
  const endFormat = eventImageSplit[eventImageSplit.length - 1];
  let imageFormatType = 'jpeg';
  if (endFormat === 'png') imageFormatType = 'png';
  if (endFormat === 'svg') imageFormatType = 'svg+xml';

  // Dates for listing show are passed differently, so they need to be handled differently :/
  const parsedEventDate = event.start_date.includes(' ')
    ? event.start_date.replace(/-/g, '/')
    : event.start_date;

  return (
    <div className="event-card-wrapper" id={id}>
      <div
        ref={cardRef}
        className={`event-card ${eventCardMap}`}
        // onMouseEnter={handleMouseEnter}
        // onMouseLeave={handleMouseLeave}
      >
        <section className="event-image-outer">
          <div
            style={{
              background:
                'linear-gradient(rgba(0, 0, 0, 0) 42%, rgba(0, 0, 0, 0.7) 100%)',
              zIndex: 1,
            }}
          />
          <picture className="background-picture">
            <source
              data-srcset={`${event.medium_featured_image_url} 606w, ${event.small_featured_image_url} 337w`}
              type={`image/${imageFormatType}`}
            />
            <img
              className="event-image lazyload"
              data-src={eventImage}
              alt={event.title}
              title={`${event.title} Image`}
            />
          </picture>
        </section>
        <button
          name="Favorite Event"
          aria-label="Favorite Event"
          ref={heartRef}
          type="button"
          className={`card-heart ${event.favorited ? 'favorited' : ''}`}
          onClick={handleFavorite}
        >
          <ReactSVG src={heart_path} className="heart-icon" />
        </button>
        <section className="event-card-info">
          <h3 className="event-title">{event.title}</h3>
          <h3 className="event-date">
            {/* Date Format: "2/18/2020" */}
            {moment(
              new Date(parsedEventDate).toLocaleDateString(),
              'MM/DD/YYYY'
            )
              .add(1, 'd')
              .format('MMM D')}
          </h3>
          <h3 className="event-info">
            <span className="category">
              {event.category.replace(/_/g, ' ')}
            </span>
            <span className="time">
              {' '}
              • {!event.all_day_event ? event.start_time : 'All Day'}
            </span>
          </h3>
        </section>
        {/* eslint-disable-next-line */}
        <a
          aria-label={`View ${event.title} event`}
          id={event.id}
          href={`${location}/events/${event.slug}/${event.id}`}
          className="hidden-background"
          onClick={() => {
            if (window.location.pathname === '/events') {
              ReactGA.event({
                category: 'Event',
                action: 'Viewed Event from Search',
              });
            }
          }}
        />
      </div>
    </div>
  );
}

EventCard.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string.isRequired,
    start_date: PropTypes.string.isRequired,
    category: PropTypes.string,
  }).isRequired,
  eventCardMap: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function EventCardWrapper({ ...props }) {
  return (
    <ErrorBoundary>
      <EventCard {...props} />
    </ErrorBoundary>
  );
}
